import { Brain, ChevronDown, CircleAlert, X } from 'lucide-react'
import type {
  AssistantMessageDisplay,
  ThinkingMessageDisplay,
  ToolCallDisplay,
} from '@/types'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Spinner } from '@/components/ui/spinner'
import { MessageResponse } from '@/components/ai/message'
import { ReasoningContent } from '@/components/ai/reasoning'
import { cn } from '@/lib/utils'
import {
  convertToolName,
  getToolAction,
  getToolDetail,
  TOOL_ACTIONS,
} from '@/utils/toolConfig'

import { CHAT_MARKDOWN_COMPONENTS } from '../ChatMarkdownLink'
import type { ToolCallGroupDisplay, WorkStep } from '../groupEvents'
import { ToolCallMessage } from '../ToolCallMessage'
import {
  TOOL_CHEVRON_CLASSNAME,
  TOOL_ICON_CLASSNAME,
  TOOL_TRIGGER_CLASSNAME,
  WORK_NEST_CLASSNAME,
} from '../toolTriggerStyles'
import {
  countFailed,
  groupSummary,
  isInFlight,
  thoughtFirstLine,
} from './utils'

function RunningSpinner() {
  return <Spinner aria-label="Running" className="size-3.5 shrink-0" />
}

function ThoughtStep({ event }: { event: ThinkingMessageDisplay }) {
  return (
    <Collapsible className="flex w-full flex-col items-start">
      <CollapsibleTrigger className={TOOL_TRIGGER_CLASSNAME}>
        <Brain aria-hidden="true" className={TOOL_ICON_CLASSNAME} />
        <span className="min-w-0 truncate">
          {thoughtFirstLine(event.content) || 'Thinking'}
        </span>
        <ChevronDown aria-hidden="true" className={TOOL_CHEVRON_CLASSNAME} />
      </CollapsibleTrigger>
      <ReasoningContent className="mt-1 mb-2 max-h-60 w-full overflow-y-auto pl-6 leading-normal">
        {event.content}
      </ReasoningContent>
    </Collapsible>
  )
}

function ToolStepLabel({
  event,
  live,
}: {
  event: ToolCallDisplay
  live: boolean
}) {
  const failed = event.status === 'error'
  const running = live && isInFlight(event)
  const Icon = TOOL_ACTIONS[getToolAction(event.toolName)].icon
  return (
    <>
      <Icon
        aria-hidden="true"
        className={cn(TOOL_ICON_CLASSNAME, failed && 'text-destructive')}
      />
      <span className="min-w-0 truncate">
        {convertToolName(event.toolName, !running)}{' '}
        <span className="font-normal text-muted-foreground">
          {getToolDetail(event.toolName, event.args)}
        </span>
      </span>
      {running && <RunningSpinner />}
      {failed && (
        <X
          aria-label="Failed"
          role="img"
          className="size-3.5 shrink-0 text-destructive"
        />
      )}
    </>
  )
}

function ToolStep({ event, live }: { event: ToolCallDisplay; live: boolean }) {
  return (
    <ToolCallMessage
      event={event}
      trigger={<ToolStepLabel event={event} live={live} />}
    />
  )
}

function ToolGroupStep({
  group,
  live,
}: {
  group: ToolCallGroupDisplay
  live: boolean
}) {
  const failed = countFailed(group.toolCalls) > 0
  const running = live && group.toolCalls.some(isInFlight)
  const first = group.toolCalls[0]
  const Icon = failed
    ? CircleAlert
    : TOOL_ACTIONS[getToolAction(first?.toolName ?? '')].icon
  return (
    <Collapsible
      className="flex w-full flex-col items-start"
      defaultOpen={failed}>
      <CollapsibleTrigger className={TOOL_TRIGGER_CLASSNAME}>
        <Icon
          aria-hidden="true"
          className={cn(TOOL_ICON_CLASSNAME, failed && 'text-destructive')}
        />
        <span className="min-w-0 truncate">
          {groupSummary(group.toolCalls)}
        </span>
        {running && <RunningSpinner />}
        <span className="shrink-0 rounded-full bg-black-alpha-5 px-1.5 text-xs font-normal text-muted-foreground tabular-nums">
          {group.toolCalls.length}
        </span>
        <ChevronDown aria-hidden="true" className={TOOL_CHEVRON_CLASSNAME} />
      </CollapsibleTrigger>
      <CollapsibleContent className={WORK_NEST_CLASSNAME}>
        {group.toolCalls.map((call) => (
          <ToolStep event={call} key={call.id} live={live} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}

function ProseStep({ event }: { event: AssistantMessageDisplay }) {
  return (
    // Always settled: a block ends at its last work event, so prose still
    // streaming trails the block instead of sitting in it.
    <MessageResponse
      className="my-1 text-sm leading-normal text-muted-foreground"
      components={CHAT_MARKDOWN_COMPONENTS}
      mode="static">
      {event.content}
    </MessageResponse>
  )
}

export function WorkSteps({
  steps,
  live,
}: {
  steps: WorkStep[]
  live: boolean
}) {
  // Only the last step can still be running: a later step means the model
  // moved on, so an earlier call whose result never arrived is not stuck.
  return steps.map((step, i) => {
    const stepLive = live && i === steps.length - 1
    if (step.type === 'assistant_message')
      return <ProseStep event={step} key={step.id} />
    if (step.type === 'thinking_message')
      return <ThoughtStep event={step} key={step.id} />
    if (step.type === 'tool_call_group')
      return <ToolGroupStep group={step} key={step.id} live={stepLive} />
    return <ToolStep event={step} key={step.id} live={stepLive} />
  })
}
